import { HttpContext, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  AuthPublicDataService,
  AuthRegistrationDataService,
  ErrorCode,
  FfdNotesInvitationAuthTwoFaDataService,
  FfdNotesInvitationDataService,
  FfdNotesInvitationNextStep,
  NotesBaaSignRequestActionDataService,
  NotesContractSource,
  ProfileRoleV2,
  PreFfdNotesInvitationDataService,
  XSource,
} from '@ffd/api-client';
import { REGISTRATION_TOKEN } from '@libs/registration';
import { TagEvent, TagPusherService, ToastService, TokenService } from '@libs/shared-services';
import { authActions, selectCurrentUserProfile, selectIsUserLoggedIn } from '@libs/store';
import { ApiHttpErrorResponse, DISABLE_GLOBAL_ERROR_TOASTS, ffdNotesRoutePrefix, routes, X_SOURCE } from '@libs/utils';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, of, switchMap, take, tap } from 'rxjs';
import * as jwtDecode from 'jwt-decode';
import { NOTES_BAA_SIGN_REQUEST_TOKEN } from '../../tokens/baa-sign-request.token';
import { BaaSignRequestResolveService } from '../../tokens/baa-sign-request-resolve.service';
import { FFD_NOTES_INVITATION_TOKEN, FFD_NOTES_INVITATION_TWO_FA_TOKEN } from '../../tokens/notes-invitation.token';
import { FfdNotesInvitationResolveService } from '../../tokens/ffd-notes-invitation-resolve.service';

interface AccountDetails {
  firstName: string;
  lastName: string;
  role: ProfileRoleV2;
  password: string;
  email?: string;
}

@Injectable()
export class BaaSignupFlowService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly authPublicDataService = inject(AuthPublicDataService);
  private readonly authRegistrationDataService = inject(AuthRegistrationDataService);
  private readonly actionDataService = inject(NotesBaaSignRequestActionDataService);
  private readonly resolveService = inject(BaaSignRequestResolveService);
  private readonly invitationResolveService = inject(FfdNotesInvitationResolveService);
  private readonly preFfdNotesInvitationDataService = inject(PreFfdNotesInvitationDataService);
  private readonly ffdNotesInvitationAuthTwoFaDataService = inject(FfdNotesInvitationAuthTwoFaDataService);
  private readonly ffdNotesInvitationDataService = inject(FfdNotesInvitationDataService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly tagPusherService = inject(TagPusherService);

  public readonly signerEmailSignal = signal<string | null>(null);
  public readonly signerNameSignal = signal<string | null>(null);
  public readonly requesterNameSignal = signal<string | null>(null);
  public readonly registrationTokenSignal = signal<string | null>(null);
  public readonly isInitializingSignal = signal(false);
  public readonly isResolveLoadingSignal = signal(false);
  public readonly isResolveErrorSignal = signal(false);
  public readonly isRegistrationLoadingSignal = signal(false);
  public readonly isOtpLoadingSignal = signal(false);
  public readonly isOtpErrorSignal = signal(false);
  public readonly isResendLoadingSignal = signal(false);
  public readonly isPracticeLoadingSignal = signal(false);

  public readonly isInvitationFlowSignal = signal(false);
  public readonly invitationTokenSignal = signal<string | null>(null);
  public readonly inviterNameSignal = signal<string | null>(null);
  public readonly siteNameSignal = signal<string | null>(null);
  public readonly invitationTwoFaTokenSignal = signal<string | null>(null);
  public readonly invitationOtpSourceSignal = signal<'register' | 'login' | null>(null);
  public readonly isLoginLoadingSignal = signal(false);

  public init(): void {}

  public initialize(): void {
    const invitationResolution = this.invitationResolveService.resolutionSignal();
    if (invitationResolution) {
      this.isInvitationFlowSignal.set(true);
      this.invitationTokenSignal.set(invitationResolution.invitationToken);
      this.inviterNameSignal.set(invitationResolution.inviterName);
      this.siteNameSignal.set(invitationResolution.siteName);
      return;
    }

    const resolution = this.resolveService.resolutionSignal();
    if (!resolution) return;

    this.isInitializingSignal.set(true);
    this.signerEmailSignal.set(resolution.signerEmail);
    this.signerNameSignal.set(resolution.signerName);
    this.requesterNameSignal.set(resolution.requesterName);

    this.store
      .select(selectIsUserLoggedIn)
      .pipe(
        tap((isLoggedIn) => {
          if (!isLoggedIn) {
            this.store.dispatch(authActions.onProfileRequested());
          }
        }),
        filter((isLoggedIn): isLoggedIn is boolean => isLoggedIn !== null),
        take(1),
        switchMap((isLoggedIn) => (isLoggedIn ? this.store.select(selectCurrentUserProfile).pipe(take(1)) : of(null))),
      )
      .subscribe((profile) => {
        if (profile) {
          this.handleLoggedInUser(profile, resolution.signerEmail);
        } else {
          this.checkInviteeEmail(resolution.signerEmail);
        }
      });
  }

  public registerWithEmail(): void {
    const email = this.signerEmailSignal();
    if (!email) return;

    this.isRegistrationLoadingSignal.set(true);
    this.authPublicDataService
      .registerWithEmail({ email }, XSource.NOTES, 'body', false, {
        context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
      })
      .subscribe({
        next: (token) => {
          this.isRegistrationLoadingSignal.set(false);
          this.registrationTokenSignal.set(token.registrationToken);
          this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow, routes.baaSignupOtp], {
            queryParamsHandling: 'preserve',
          });
        },
        error: (err: ApiHttpErrorResponse) => {
          this.isRegistrationLoadingSignal.set(false);
          if (err.status === HttpStatusCode.Conflict || err.error?.code === ErrorCode.EMAIL_ALREADY_REGISTERED) {
            const redirectUrl = this.router.url;
            this.toastService.info(this.translateService.instant('ffd_notes_invitation_email_already_registered'));
            this.router.navigate([...ffdNotesRoutePrefix(), routes.login], { queryParams: { redirectUrl } });
            return;
          }

          console.error('BaaSignupFlowService registerWithEmail error:', err);
          this.toastService.error(this.translateService.instant('login_other_error'));
        },
      });
  }

  public saveAccountDetails(details: AccountDetails): void {
    if (this.isInvitationFlowSignal()) {
      this.isRegistrationLoadingSignal.set(true);
      this.preFfdNotesInvitationDataService
        .ffdNotesInvitationRegisterV2(
          {
            firstName: details.firstName,
            lastName: details.lastName,
            profileRoleV2: details.role,
            password: details.password,
            email: details.email ?? '',
          },
          'body',
          false,
          {
            context: new HttpContext().set(FFD_NOTES_INVITATION_TOKEN, this.invitationTokenSignal()).set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          },
        )
        .subscribe({
          next: ({ twoFaToken, twoFaEmail }) => {
            this.isRegistrationLoadingSignal.set(false);
            this.tagPusherService.pushTag({ event: TagEvent.FfdNotesSignUp, platform: 'web' });
            this.invitationTwoFaTokenSignal.set(twoFaToken);
            this.invitationOtpSourceSignal.set('register');
            this.signerEmailSignal.set(twoFaEmail);
            this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow, routes.baaSignupOtp], {
              queryParamsHandling: 'preserve',
            });
          },
          error: (err: ApiHttpErrorResponse) => {
            this.isRegistrationLoadingSignal.set(false);

            if (err.status === HttpStatusCode.Conflict || err.error?.code === ErrorCode.EMAIL_ALREADY_REGISTERED) {
              this.toastService.info(this.translateService.instant('ffd_notes_invitation_email_already_registered'));
              this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow, routes.baaSignupLogin], {
                queryParamsHandling: 'preserve',
              });
              return;
            }

            console.error('BaaSignupFlowService saveAccountDetails (invitation) error:', err);
            this.toastService.error(this.translateService.instant('login_other_error'));
          },
        });
      return;
    }

    const registrationToken = this.registrationTokenSignal();
    if (!registrationToken) return;

    this.isRegistrationLoadingSignal.set(true);
    this.authRegistrationDataService
      .registerPersonalDetailsV2(
        {
          firstName: details.firstName,
          lastName: details.lastName,
          profileRoleV2: details.role,
          password: details.password,
        },
        'body',
        false,
        {
          context: new HttpContext()
            .set(REGISTRATION_TOKEN, registrationToken)
            .set(X_SOURCE, XSource.NOTES)
            .set(DISABLE_GLOBAL_ERROR_TOASTS, true),
        },
      )
      .subscribe({
        next: ({ accessToken, refreshToken }) => {
          this.isRegistrationLoadingSignal.set(false);
          this.tagPusherService.pushTag({ event: TagEvent.FfdNotesSignUp, platform: 'web' });
          if (accessToken && refreshToken) {
            const accessTokenExpirationTimestamp = jwtDecode.jwtDecode(accessToken).exp;
            const refreshTokenExpirationTimestamp = jwtDecode.jwtDecode(refreshToken).exp;
            if (accessTokenExpirationTimestamp && refreshTokenExpirationTimestamp) {
              this.tokenService.authenticated({
                accessToken,
                refreshToken,
                accessTokenExpirationTimestamp,
                refreshTokenExpirationTimestamp,
              });
            }
          }
          this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow, routes.baaSignupPracticeDetails], {
            queryParamsHandling: 'preserve',
          });
        },
        error: (err) => {
          this.isRegistrationLoadingSignal.set(false);
          console.error('BaaSignupFlowService saveAccountDetails error:', err);
          // This payload carries no email, so a 409 email-conflict can't occur here.
          this.toastService.error(this.translateService.instant('login_other_error'));
        },
      });
  }

  public verifyOtp(_payload: { otp: string }): void {
    if (this.isOtpLoadingSignal()) return;

    if (this.isInvitationFlowSignal()) {
      this.isOtpLoadingSignal.set(true);
      this.isOtpErrorSignal.set(false);
      this.ffdNotesInvitationAuthTwoFaDataService
        .ffdNotesInvitationAuthVerify2fa({ code: _payload.otp }, 'body', false, { context: this.invitationTwoFaContext() })
        .subscribe({
          next: ({ accessToken, refreshToken }) => {
            this.isOtpLoadingSignal.set(false);
            this.authenticateAndCreateJoinRequest(accessToken, refreshToken);
          },
          error: (err) => {
            this.isOtpLoadingSignal.set(false);
            this.isOtpErrorSignal.set(true);
            console.error('BaaSignupFlowService verifyOtp (invitation) error:', err);
          },
        });
      return;
    }

    const registrationToken = this.registrationTokenSignal();
    if (!registrationToken) return;

    this.isOtpLoadingSignal.set(true);
    this.isOtpErrorSignal.set(false);
    this.authRegistrationDataService
      .verifyRegistrationEmailWithOtp({ otp: _payload.otp }, 'body', false, {
        context: new HttpContext().set(REGISTRATION_TOKEN, registrationToken),
      })
      .subscribe({
        next: () => {
          this.isOtpLoadingSignal.set(false);
          this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow, routes.baaSignupAccountDetails], {
            queryParamsHandling: 'preserve',
          });
        },
        error: (err) => {
          this.isOtpLoadingSignal.set(false);
          this.isOtpErrorSignal.set(true);
          console.error('BaaSignupFlowService verifyOtp error:', err);
          // TODO: add proper error handling
        },
      });
  }

  public resendOtp(): void {
    if (this.isInvitationFlowSignal()) {
      this.isResendLoadingSignal.set(true);
      this.ffdNotesInvitationAuthTwoFaDataService
        .resendFfdNotesInvitationAuthTwoFaCode('body', false, { context: this.invitationTwoFaContext() })
        .subscribe({
          next: () => {
            this.isResendLoadingSignal.set(false);
          },
          error: (err) => {
            this.isResendLoadingSignal.set(false);
            console.error('BaaSignupFlowService resendOtp (invitation) error:', err);
          },
        });
      return;
    }

    const registrationToken = this.registrationTokenSignal();
    if (!registrationToken) return;

    this.isResendLoadingSignal.set(true);
    this.authRegistrationDataService
      .resendRegistrationEmailVerification('body', false, {
        context: new HttpContext().set(REGISTRATION_TOKEN, registrationToken).set(X_SOURCE, XSource.NOTES),
      })
      .subscribe({
        next: (token) => {
          this.isResendLoadingSignal.set(false);
          this.registrationTokenSignal.set(token.registrationToken);
        },
        error: (err) => {
          this.isResendLoadingSignal.set(false);
          console.error('BaaSignupFlowService resendOtp error:', err);
          // TODO: add proper error handling
        },
      });
  }

  public resetOtpVerification(): void {
    this.isOtpErrorSignal.set(false);
  }

  public loginForInvitation(credentials: { email: string; password: string }): void {
    this.isLoginLoadingSignal.set(true);
    this.preFfdNotesInvitationDataService
      .ffdNotesInvitationLogin({ ...credentials, shortLivedSession: true }, 'body', false, {
        context: new HttpContext().set(FFD_NOTES_INVITATION_TOKEN, this.invitationTokenSignal()).set(DISABLE_GLOBAL_ERROR_TOASTS, true),
      })
      .subscribe({
        next: ({ twoFaToken, twoFaEmail, accessToken, refreshToken, nextStep }) => {
          this.isLoginLoadingSignal.set(false);
          if (nextStep === FfdNotesInvitationNextStep.AUTH_TWO_FA) {
            this.invitationTwoFaTokenSignal.set(twoFaToken ?? null);
            this.invitationOtpSourceSignal.set('login');
            this.signerEmailSignal.set(twoFaEmail);
            this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow, routes.baaSignupOtp], {
              queryParamsHandling: 'preserve',
            });
            return;
          }
          this.authenticateAndCreateJoinRequest(accessToken, refreshToken);
        },
        error: (err: ApiHttpErrorResponse) => {
          this.isLoginLoadingSignal.set(false);
          const errorCode = err.error?.code;

          if (errorCode === ErrorCode.ALREADY_ASSOCIATED) {
            this.toastService.error(this.translateService.instant('ffd_notes_invitation_login_already_associated'));
            this.router.navigate([...ffdNotesRoutePrefix(), routes.baaRequestFlow], { queryParamsHandling: 'preserve' });
            return;
          }

          if (errorCode === ErrorCode.LOCKED_OUT) {
            this.router.navigate([...ffdNotesRoutePrefix(), routes.login, routes.loginLockout, routes.loginUnlockAccount]);
            return;
          }

          if (errorCode === ErrorCode.VERIFICATION_FAILED) {
            this.toastService.error(this.translateService.instant('login_cannot_find_account_error'));
            return;
          }

          console.error('BaaSignupFlowService loginForInvitation error:', err);
          this.toastService.error(this.translateService.instant('login_other_error'));
        },
      });
  }

  public submitPracticeDetails(details: { legalName: string; address: string; city: string; state: string; zip: string }): void {
    const token = this.resolveService.notesBaaSignRequestTokenSignal();
    this.isPracticeLoadingSignal.set(true);

    this.actionDataService
      .completeNotesBaaSignRequest({ ...details, country: 'US', source: NotesContractSource.WEB }, undefined, undefined, {
        context: new HttpContext().set(NOTES_BAA_SIGN_REQUEST_TOKEN, token),
      })
      .subscribe({
        next: () => {
          this.isPracticeLoadingSignal.set(false);
          this.resolveService.markSignRequestCompleted();
          this.router.navigate([...ffdNotesRoutePrefix(), routes.baaRequestFlow], {
            queryParamsHandling: 'preserve',
          });
        },
        error: (err) => {
          this.isPracticeLoadingSignal.set(false);
          console.error('BaaSignupFlowService submitPracticeDetails error:', err);
          // TODO: add proper error handling
        },
      });
  }

  private invitationTwoFaContext(): HttpContext {
    return new HttpContext()
      .set(FFD_NOTES_INVITATION_TOKEN, this.invitationTokenSignal())
      .set(FFD_NOTES_INVITATION_TWO_FA_TOKEN, this.invitationTwoFaTokenSignal())
      .set(X_SOURCE, XSource.NOTES);
  }

  private authenticateAndCreateJoinRequest(accessToken?: string, refreshToken?: string): void {
    if (accessToken && refreshToken) {
      const accessTokenExpirationTimestamp = jwtDecode.jwtDecode(accessToken).exp;
      const refreshTokenExpirationTimestamp = jwtDecode.jwtDecode(refreshToken).exp;
      if (accessTokenExpirationTimestamp && refreshTokenExpirationTimestamp) {
        this.tokenService.authenticated({
          accessToken,
          refreshToken,
          accessTokenExpirationTimestamp,
          refreshTokenExpirationTimestamp,
        });
      }
    }

    this.ffdNotesInvitationDataService
      .createFfdNotesInvitationJoinRequest(undefined, undefined, {
        context: new HttpContext().set(FFD_NOTES_INVITATION_TOKEN, this.invitationTokenSignal()),
      })
      .subscribe({
        next: () => this.router.navigate([...ffdNotesRoutePrefix(), routes.ffdNotesInviteSuccess], { queryParamsHandling: 'preserve' }),
        error: (err) => {
          console.error('BaaSignupFlowService createFfdNotesInvitationJoinRequest error:', err);
          this.router.navigate([...ffdNotesRoutePrefix(), routes.ffdNotesInviteSuccess], { queryParamsHandling: 'preserve' });
        },
      });
  }

  private handleLoggedInUser(profile: { email: string; ffdNotesBaaSigned: boolean }, signerEmail: string): void {
    if (profile.email !== signerEmail) {
      this.tokenService.logout(true);
      this.checkInviteeEmail(signerEmail);
      return;
    }

    this.isInitializingSignal.set(false);
    if (profile.ffdNotesBaaSigned) {
      this.router.navigate([...ffdNotesRoutePrefix(), routes.baaRequestFlow], { queryParamsHandling: 'preserve' });
    } else {
      this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow, routes.baaSignupPracticeDetails], {
        queryParamsHandling: 'preserve',
      });
    }
  }

  private checkInviteeEmail(email: string): void {
    const token = this.resolveService.notesBaaSignRequestTokenSignal();
    this.preFfdNotesInvitationDataService
      .checkFfdNotesInvitationInviteeEmail(email, 'body', false, {
        context: new HttpContext().set(NOTES_BAA_SIGN_REQUEST_TOKEN, token).set(DISABLE_GLOBAL_ERROR_TOASTS, true),
      })
      .subscribe({
        next: ({ emailRecognised }) => {
          this.isInitializingSignal.set(false);
          if (emailRecognised) {
            const redirectUrl = this.router.url;
            this.toastService.info(this.translateService.instant('ffd_notes_invitation_email_already_registered'));
            this.router.navigate([...ffdNotesRoutePrefix(), routes.login], { queryParams: { redirectUrl } });
            return;
          }

          this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow, routes.baaSignupInfo], {
            queryParamsHandling: 'preserve',
          });
        },
        error: (err: ApiHttpErrorResponse) => {
          this.isInitializingSignal.set(false);
          console.error('BaaSignupFlowService checkInviteeEmail error:', err);
          this.toastService.error(this.translateService.instant('baa_request_link_invalid_error'));
          this.router.navigate([...ffdNotesRoutePrefix(), routes.login]);
        },
      });
  }
}
